import React, { useState, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Input,
  Select,
  Typography,
  Button,
  Space,
  Tag
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined,
  CloseCircleOutlined,
  ShopOutlined,
  TagOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;
const { Option } = Select;

// Default filter values
const DEFAULT_FILTERS = {
  searchText: '',
  selectedStatus: 'all',
  selectedShopifyStore: 'all'
};

const ShopifyProductsFilter = ({
  onSearch,
  onStatusChange,
  onShopifyStoreChange,
  searchText,
  selectedStatus,
  selectedShopifyStore,
  loading
}) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Get Shopify stores from Redux store (will be added later)
  const { shopifyStores } = useSelector(state => state.dataCommon || { shopifyStores: [] });

  const [localFilters, setLocalFilters] = useState({
    searchText: searchText || '',
    selectedStatus: selectedStatus || 'all',
    selectedShopifyStore: selectedShopifyStore || 'all'
  });

  // Sync local filters with props when they change externally
  React.useEffect(() => {
    setLocalFilters({
      searchText: searchText || '',
      selectedStatus: selectedStatus || 'all',
      selectedShopifyStore: selectedShopifyStore || 'all'
    });
  }, [searchText, selectedStatus, selectedShopifyStore]);

  // Handle individual filter changes (only update local state)
  const handleLocalFilterChange = useCallback((filterKey, value) => {
    setLocalFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  // Handle search input change (only update local state)
  const handleSearchInputChange = useCallback((e) => {
    handleLocalFilterChange('searchText', e.target.value);
  }, [handleLocalFilterChange]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setLocalFilters(DEFAULT_FILTERS);
    onSearch('');
    onStatusChange('all');
    onShopifyStoreChange('all');
    setIsFilterExpanded(false);
  }, [onSearch, onStatusChange, onShopifyStoreChange]);

  // Handle apply filters
  const handleApplyFilters = useCallback(() => {
    onSearch(localFilters.searchText);
    onStatusChange(localFilters.selectedStatus);
    onShopifyStoreChange(localFilters.selectedShopifyStore);
    setIsFilterExpanded(false);
  }, [localFilters, onSearch, onStatusChange, onShopifyStoreChange]);

  // Handle search on Enter
  const handleSearchOnEnter = useCallback(() => {
    onSearch(localFilters.searchText);
  }, [localFilters.searchText, onSearch]);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setLocalFilters(prev => ({ ...prev, searchText: '' }));
    onSearch('');
  }, [onSearch]);

  // Handle reload (apply current filters)
  const handleReload = useCallback(() => {
    handleApplyFilters();
  }, [handleApplyFilters]);

  // Individual clear functions for specific filters
  const clearIndividualFilter = useCallback((filterKey) => {
    const defaultValue = DEFAULT_FILTERS[filterKey];
    handleLocalFilterChange(filterKey, defaultValue);
  }, [handleLocalFilterChange]);

  // Check if local filters have changes compared to applied filters
  const hasUnappliedChanges = useMemo(() => {
    return localFilters.searchText !== (searchText || '') ||
      localFilters.selectedStatus !== (selectedStatus || 'all') ||
      localFilters.selectedShopifyStore !== (selectedShopifyStore || 'all');
  }, [localFilters, searchText, selectedStatus, selectedShopifyStore]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchText && searchText !== '') count++;
    if (selectedStatus && selectedStatus !== 'all') count++;
    if (selectedShopifyStore && selectedShopifyStore !== 'all') count++;
    return count;
  }, [searchText, selectedStatus, selectedShopifyStore]);

  // Render filter summary
  const renderFilterSummary = useMemo(() => {
    const filters = [];

    if (searchText && searchText !== '') {
      filters.push(
        <Tag key="search" color="blue">
          Search: {searchText}
        </Tag>
      );
    }

    if (selectedStatus && selectedStatus !== 'all') {
      const statusLabels = {
        'active': 'Active',
        'draft': 'Draft',
        'archived': 'Archived'
      };
      filters.push(
        <Tag key="status" color="green">
          Status: {statusLabels[selectedStatus] || selectedStatus}
        </Tag>
      );
    }

    if (selectedShopifyStore && selectedShopifyStore !== 'all') {
      const store = shopifyStores?.find(s => s.id === selectedShopifyStore);
      filters.push(
        <Tag key="shopifyStore" color="orange">
          Store: {store?.storeName || selectedShopifyStore}
        </Tag>
      );
    }

    return filters;
  }, [searchText, selectedStatus, selectedShopifyStore, shopifyStores]);

  return (
    <div style={{ paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
      {/* Basic Search Row */}
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <Row>
            <Col span={6}>
              <div style={{ position: 'relative' }}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#999' }} />}
                  placeholder="Search by product title, ID or type"
                  value={localFilters.searchText}
                  onChange={handleSearchInputChange}
                  onPressEnter={handleSearchOnEnter}
                  style={{ width: '100%', backgroundColor: 'transparent' }}
                  variant='underlined'
                  size="middle"
                />
                {localFilters.searchText && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={handleClearSearch}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#999',
                      padding: 0,
                      minWidth: 'auto',
                      zIndex: 1
                    }}
                    title="Clear search and reload"
                  />
                )}
              </div>
              {/* Show hint when user has unsaved changes */}
              {hasUnappliedChanges && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    fontStyle: 'italic',
                    color: '#fa8c16'
                  }}
                >
                  You have unsaved filter changes. Click "Apply Filters" to search.
                </Text>
              )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReload}
              loading={loading}
              title="Reload with current filters"
            >
              Reload
            </Button>
            <Button
              type={isFilterExpanded ? "primary" : "default"}
              icon={<FilterOutlined />}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              Filters
              {activeFiltersCount > 0 && (
                <span style={{
                  marginLeft: 4,
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  borderRadius: '50%',
                  width: 18,
                  height: 18,
                  display: 'inline-block',
                  textAlign: 'center',
                  fontSize: 12,
                  lineHeight: '18px'
                }}>
                  {activeFiltersCount}
                </span>
              )}
              {hasUnappliedChanges && (
                <span style={{
                  marginLeft: 4,
                  backgroundColor: '#fa8c16',
                  color: 'white',
                  borderRadius: '50%',
                  width: 6,
                  height: 6,
                  display: 'inline-block'
                }} title="You have unsaved changes">
                </span>
              )}
              {isFilterExpanded ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
            </Button>
            {activeFiltersCount > 0 && (
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear Filters
              </Button>
            )}
            <Button
              type="primary"
              onClick={handleApplyFilters}
              disabled={!hasUnappliedChanges}
            >
              Apply Filters
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Active Filters Summary */}
      {activeFiltersCount > 0 && !isFilterExpanded && (
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
              Active filters:
            </Text>
            <Space size={4} wrap>
              {renderFilterSummary}
            </Space>
          </Col>
        </Row>
      )}

      {/* Advanced Filters */}
      {isFilterExpanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f0f0f0' }}>
          <Row gutter={[16, 16]}>
            {/* Shopify Store Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>
                  <ShopOutlined style={{ marginRight: 4 }} />
                  Shopify Store
                </Text>
                {localFilters.selectedShopifyStore !== 'all' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedShopifyStore')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear store filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                onChange={(value) => handleLocalFilterChange('selectedShopifyStore', value)}
                value={localFilters.selectedShopifyStore}
                placeholder="Select Shopify store"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                loading={!shopifyStores}
              >
                <Option value="all">All Stores</Option>
                {shopifyStores && shopifyStores.map(store => (
                  <Option key={store.id} value={store.id}>
                    {store.storeName} ({store.shopifyDomain})
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Status Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Status
                </Text>
                {localFilters.selectedStatus !== 'all' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedStatus')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear status filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                onChange={(value) => handleLocalFilterChange('selectedStatus', value)}
                value={localFilters.selectedStatus}
                placeholder="Select status"
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active</Option>
                <Option value="draft">Draft</Option>
                <Option value="archived">Archived</Option>
              </Select>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default ShopifyProductsFilter;
